import type { Crianca, FilhoDoBanco, Remedio } from './types';

const VAZIOS = ['', '-', '—', 'nao', 'não', 'n/a', 'nn'];

function normalizar(v?: string | null): string {
  return (v ?? '').trim();
}

function comparar(v: string): string {
  return v
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function novaChave(): string {
  return `c${Math.random().toString(36).slice(2, 9)}`;
}

export function novoRemedio(): Remedio {
  return { nome: '', dose: '', horarios: '' };
}

export function novaCrianca(): Crianca {
  return {
    chave: novaChave(),
    nome: '',
    nascimento: '',
    temAlergia: null,
    alergiaTexto: '',
    temRestricao: null,
    alimentacaoTexto: '',
    tomaRemedio: null,
    remedios: [],
    autorizaFebre: null,
    febreTexto: '',
    saude: '',
  };
}

export function mascaraCpf(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export function mascaraDataBr(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/** dd/mm/aaaa → AAAA-MM-DD (vazio se não estiver completa). */
export function dataParaIso(v: string): string {
  const d = v.replace(/\D/g, '');
  if (d.length !== 8) return '';
  return `${d.slice(4, 8)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
}

export function dataValida(v: string): boolean {
  const iso = dataParaIso(v);
  if (!iso) return false;
  const [ano, mes, dia] = iso.split('-').map((n) => parseInt(n, 10));
  if (ano < 1900 || ano > 2100) return false;
  if (mes < 1 || mes > 12) return false;
  const ultimoDia = new Date(ano, mes, 0).getDate();
  return dia >= 1 && dia <= ultimoDia;
}

/** Qualquer data (ISO ou dd/mm/aaaa) → dd/mm/aaaa para a tela. */
export function paraDataBr(valor?: string | null): string {
  const v = normalizar(valor);
  if (!v) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
    const [ano, mes, dia] = v.slice(0, 10).split('-');
    return `${dia}/${mes}/${ano}`;
  }
  return mascaraDataBr(v);
}

export function textoParaRemedios(valor?: string | null): Remedio[] {
  const v = normalizar(valor);
  const t = comparar(v);
  if (!v || t === 'nenhum' || t === 'nenhuma' || t.startsWith('nao toma')) {
    return [];
  }
  return v
    .split(';')
    .map((linha) => linha.trim())
    .filter(Boolean)
    .map((linha) => {
      const partes = linha
        .split(/\s*[—–]\s*|\s+-\s+/)
        .map((p) => p.trim())
        .filter((p) => p.length > 0);
      return {
        nome: partes[0] ?? '',
        dose: partes[1] ?? '',
        horarios: partes.slice(2).join(' ') ?? '',
      };
    });
}

export function remediosParaTexto(lista: Remedio[]): string {
  const validos = lista.filter((r) => r.nome.trim());
  if (!validos.length) return 'Nenhum';
  return validos
    .map((r) =>
      [r.nome.trim(), r.dose.trim(), r.horarios.trim()].filter((p) => p.length > 0).join(' — '),
    )
    .join('; ');
}

/** Converte a criança do banco para o estado editável da tela. */
export function deFilhoDoBanco(filho: FilhoDoBanco): Crianca {
  const base = novaCrianca();
  const nome = normalizar(filho.Nome);
  const nascimento = paraDataBr(filho['Data de nascimento']);

  const alergias = normalizar(filho.Alergias);
  const alergiaComparada = comparar(alergias);
  const semAlergia =
    VAZIOS.includes(alergiaComparada) ||
    alergiaComparada.startsWith('nao tem') ||
    alergiaComparada.startsWith('nenhum') ||
    alergiaComparada.startsWith('nenhuma') ||
    alergiaComparada.startsWith('sem alergia') ||
    alergiaComparada === 'nada';

  const alimentacao = normalizar(filho['Alimentação']);
  const comeDeTudo =
    comparar(alimentacao) === 'come de tudo' ||
    comparar(alimentacao) === 'sem restricao' ||
    comparar(alimentacao) === 'sem restricoes';

  const remedios = textoParaRemedios(filho['Remédios e horários']);
  const remediosTexto = normalizar(filho['Remédios e horários']);
  const remedioComparado = comparar(remediosTexto);
  const semRemedio =
    VAZIOS.includes(remedioComparado) ||
    remedioComparado.startsWith('nao toma') ||
    remedioComparado.startsWith('nenhum') ||
    remedioComparado.startsWith('nenhuma');

  const febre = normalizar(filho['Remédio para febre ou dor']);
  const semFebre =
    VAZIOS.includes(comparar(febre)) ||
    comparar(febre).startsWith('nao autoriz') ||
    comparar(febre).startsWith('nao');
  const textoFebre = febre.replace(/^sim\s*:\s*/i, '').trim();

  return {
    ...base,
    nome,
    nascimento,
    idade: filho.Idade != null ? String(filho.Idade) : undefined,
    temAlergia: !alergias ? null : semAlergia ? false : true,
    alergiaTexto: !alergias || semAlergia ? '' : alergias,
    temRestricao: !alimentacao ? null : comeDeTudo ? false : true,
    alimentacaoTexto: !alimentacao || comeDeTudo ? '' : alimentacao,
    tomaRemedio: !remediosTexto ? null : semRemedio ? false : true,
    remedios: remedios.length ? remedios : [],
    autorizaFebre: !febre ? null : semFebre ? false : true,
    febreTexto: !febre || semFebre ? '' : textoFebre,
    saude: normalizar(filho['Saúde e cuidados']),
  };
}

export function primeiroNome(nome?: string | null): string {
  const v = normalizar(nome);
  if (!v) return '';
  return v.split(/\s+/)[0];
}